import React, { useMemo, useState } from "react";
import "./mobile-menu.css";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "@/components/ui-components/icon";
import type { Category } from "@/data/categories";
import type { LocaleCode } from "@/i18n/locales";
import type { InterpolationValues } from "@/i18n/types";
import { CategoryIcon } from "@/components/ui-components/category-icon/category-icon";

interface Props {
    categories: Category[];
    locale: LocaleCode;
    t: (key: string, values?: InterpolationValues) => string;
    onNavigate: () => void;
}

export function MobileMenu({ categories, locale, t, onNavigate }: Props) {
    const location = useLocation();
    const homePath = `/${locale}`;
    const offersPath = `/${locale}/special-offers`;
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => new Set());
    const categoryGroups = useMemo(() => {
        const primary = categories.filter(category => !category.breadcrumbs?.length || category.breadcrumbs[0]?.id === category.id);
        const primaryIds = new Set(primary.map(category => category.id));
        const childrenByParent = new Map<string, Category[]>();

        for (const category of categories) {
            if (primaryIds.has(category.id)) continue;
            const parentId = category.breadcrumbs?.at(-2)?.id;
            if (!parentId || !primaryIds.has(parentId)) continue;
            const currentChildren = childrenByParent.get(parentId) ?? [];
            currentChildren.push(category);
            childrenByParent.set(parentId, currentChildren);
        }

        return primary.map(category => {
            const apiChildren = [...(category.children ?? []), ...(childrenByParent.get(category.id) ?? [])];
            const fallbackChildren: Category[] = (category.subcategories ?? []).map(subcategory => ({
                id: subcategory.id,
                icon: "",
                count: subcategory.count,
                translationKey: subcategory.translationKey,
            }));
            const children = [...apiChildren, ...fallbackChildren];
            return { category, children: [...new Map(children.map(child => [child.id, child])).values()] };
        });
    }, [categories]);

    const expandCategory = (categoryId: string) => {
        setExpandedCategories(current => {
            const next = new Set(current);
            next.add(categoryId);
            return next;
        });
    };

    return (
        <nav id="mobile-shop-menu" className="plain-menu" aria-label={t("layout.sidebar.title")}>
            <Link className={`nav-link${location.pathname === homePath ? " active" : ""}`} to={homePath} onClick={onNavigate}>
                <span className="nav-icon"><Icon name="home" size="sm" /></span>
                <span>{t("layout.sidebar.homePage")}</span>
            </Link>
            {categoryGroups.map(({ category, children: subcategories }) => {
                const path = `/${locale}/products/${category.id}`;
                const categoryName = category.name ?? (category.translationKey ? t(category.translationKey) : "");
                const isExpanded = expandedCategories.has(category.id) || location.pathname === path || subcategories.some(subcategory => location.pathname === `/${locale}/products/${subcategory.id}`);
                const subcategoryId = `mobile-subcategories-${category.id}`;
                return (
                <div className="mobile-category" key={category.id}>
                    <div className="mobile-category__row">
                        <Link className={`nav-link${location.pathname === path ? " active" : ""}`} to={path} onClick={() => {
                            if (subcategories.length > 0) expandCategory(category.id);
                            else onNavigate();
                        }}>
                            <span className="nav-icon"><CategoryIcon categoryId={category.id} /></span>
                            <span>{categoryName}</span>
                            <span className="nav-count">{category.count}</span>
                            {subcategories.length > 0 && <span className={`nav-caret${isExpanded ? " is-expanded" : ""}`} aria-hidden="true"><Icon name="chevron-right" size="sm" /></span>}
                        </Link>
                    </div>
                    {subcategories.length > 0 && isExpanded && <div className="mobile-subcategories" id={subcategoryId}>
                        {subcategories.map(subcategory => {
                            const subcategoryPath = `/${locale}/products/${subcategory.id}`;
                            const subcategoryName = subcategory.name ?? (subcategory.translationKey ? t(subcategory.translationKey) : "");
                            return <Link className={`mobile-subcategory${location.pathname === subcategoryPath ? " active" : ""}`} to={subcategoryPath} key={subcategory.id} onClick={onNavigate}><span>{subcategoryName}</span><span className="nav-count">{subcategory.count}</span></Link>;
                        })}
                    </div>}
                </div>
                );
            })}
            <div className="sidebar-divider" />
            <Link className={`nav-link${location.pathname === offersPath ? " active" : ""}`} to={offersPath} onClick={onNavigate}>
                <span className="nav-icon"><Icon name="tag" size="sm" /></span>
                <span>{t("layout.sidebar.deals")}</span>
            </Link>
        </nav>
    );
}
